import type { ConsistencyDay } from '@anytracker/core'

import { formatDay } from '../format/dates.js'
import styles from './CoverageHeatmap.module.css'

interface Props {
  /** From core's `coverageDays`, across the resolved range. */
  readonly days: readonly ConsistencyDay[]
  /** The same facts in words, beside the title. Required — see below. */
  readonly caption: string
}

/** Up to six weeks is drawn as a calendar; longer ranges as one strip. */
const CALENDAR_MAX_DAYS = 42

const WEEKDAYS = ['M', 'T', 'W', 'T', 'F', 'S', 'S'] as const

/**
 * Monday-first weekday of a `YYYY-MM-DD` key, 0 to 6.
 *
 * Only where the first cell sits in the grid. The key is a calendar date
 * already resolved in its own zone by core, so reading it as a UTC date gives
 * its weekday without any zone arithmetic.
 */
function weekdayOf(key: string): number {
  const [year, month, day] = key.split('-').map(Number)
  const sunday0 = new Date(Date.UTC(year ?? 1970, (month ?? 1) - 1, day ?? 1)).getUTCDay()
  return (sunday0 + 6) % 7
}

/**
 * Where the gaps fell (T-906).
 *
 * "22 of 30 days" is a number the report already prints; what it cannot show is
 * whether the eight missing days were one bad fortnight or a steady drizzle,
 * and that is a different fact about the same data.
 *
 * **Hidden from assistive technology, and the caption is not optional.** A grid
 * of ninety unlabelled cells is noise read aloud and each cell announced
 * separately is worse than silence — so the sentence is the only accessible
 * form of this, which makes it a required prop rather than a convention.
 *
 * A short range is laid out as a calendar under weekday initials, because a
 * month reads best the way months are usually read. A long one stays a single
 * strip whose cells shrink to fit: ninety days across a phone is about three
 * pixels each, unreadable as a value and still legible as a *shape*.
 */
export function CoverageHeatmap({ days, caption }: Props) {
  const first = days[0]
  const last = days.at(-1)
  const calendar = days.length > 0 && days.length <= CALENDAR_MAX_DAYS
  const leading = calendar && first !== undefined ? weekdayOf(first.dayKey) : 0

  return (
    <section className={styles['coverage']} aria-labelledby="coverage-title" data-bleed>
      <div className={styles['head']}>
        <h2 id="coverage-title" className={styles['title']}>
          Coverage
        </h2>
        <p className={styles['caption']}>{caption}</p>
      </div>

      {calendar ? (
        <div className={styles['calendar']} aria-hidden="true">
          {WEEKDAYS.map((initial, index) => (
            <span key={index} className={styles['weekday']}>
              {initial}
            </span>
          ))}
          <div
            className={styles['calendarCells']}
            data-testid="coverage-grid"
            aria-hidden="true"
          >
            {Array.from({ length: leading }, (_, index) => (
              <span key={`blank-${String(index)}`} className={styles['blank']} />
            ))}
            {days.map((day) => (
              <span key={day.dayKey} className={styles['cell']} data-band={day.band} />
            ))}
          </div>
        </div>
      ) : (
        <div className={styles['grid']} data-testid="coverage-grid" aria-hidden="true">
          {days.map((day) => (
            <span key={day.dayKey} className={styles['cell']} data-band={day.band} />
          ))}
        </div>
      )}

      {!calendar && first !== undefined && last !== undefined && (
        <p className={styles['ends']} aria-hidden="true">
          <span>{formatDay(first.dayKey)}</span>
          <span>{formatDay(last.dayKey)}</span>
        </p>
      )}
      {/* The scale in words, because the colours alone assert an order nobody
          was told about. */}
      <p className={styles['legend']} aria-hidden="true">
        <span>Fewer</span>
        <span className={styles['legendCells']}>
          {[0, 1, 2, 3].map((band) => (
            <span key={band} className={styles['cell']} data-band={band} />
          ))}
        </span>
        <span>More</span>
      </p>
    </section>
  )
}
